import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import TherapistReportUploader from './TherapistReportUploader';
import { deleteTherapistReportV2, getStudentEvidenceBundleV2 } from '../lib/api';
import { Icons } from '../lib/Icons';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';

const REPORT_LABELS = {
  occupational_therapy: 'Occupational Therapy',
  educational_psychology: 'Educational Psychologist',
  speech_language: 'Speech and Language',
  other: 'Other professional report',
};

function ListSection({ title, items }) {
  if (!items?.length) return null;
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{title}</p>
      <ul className="mt-1 list-disc pl-5 text-sm text-gray-800 space-y-1">
        {items.map((item, idx) => (
          <li key={`${title}-${idx}`}>{item}</li>
        ))}
      </ul>
    </div>
  );
}

export default function ProfessionalRecommendations({ studentId }) {
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState([]);
  const [error, setError] = useState('');
  const [deletingJobId, setDeletingJobId] = useState(null);

  const load = useCallback(async () => {
    if (!studentId) return;
    setLoading(true);
    setError('');
    try {
      const result = await getStudentEvidenceBundleV2({ studentId });
      setReports(result?.bundle?.therapistReports || []);
    } catch (e) {
      console.warn('Failed to load professional reports', e);
      setError('Could not load professional reports.');
      setReports([]);
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async (report) => {
    if (!report?.jobId) return;
    const typeLabel = REPORT_LABELS[report.reportType] || report.reportType || 'report';
    const confirmed = window.confirm(
      `Remove this ${typeLabel} extraction? The PDF and structured recommendations will be deleted from this student's evidence.`
    );
    if (!confirmed) return;

    setDeletingJobId(report.jobId);
    setError('');
    try {
      await deleteTherapistReportV2({ jobId: report.jobId, studentId });
      toast.success('Professional report removed');
      await load();
    } catch (e) {
      console.error('Failed to delete professional report', e);
      setError(e?.message || 'Could not delete this report.');
      toast.error('Failed to remove report');
    } finally {
      setDeletingJobId(null);
    }
  };

  return (
    <div className="space-y-6">
      <TherapistReportUploader studentId={studentId} onProcessingComplete={load} />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Icons.ClipboardDocumentList className="w-5 h-5" />
            Extracted professional reports
          </CardTitle>
          <p className="text-sm text-gray-600">
            Upload OT, EP, or SaLT reports. Structured recommendations feed classroom strategy review on the Overview tab
            and EHCNA evidence. Intervention recommendations are flagged to SENCO on Overview.
          </p>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-gray-500">Loading reports…</p>
          ) : error ? (
            <p className="text-sm text-red-600">{error}</p>
          ) : reports.length === 0 ? (
            <p className="text-sm text-gray-500">No professional reports uploaded yet.</p>
          ) : (
            <ul className="divide-y rounded-lg border">
              {reports.map((report) => {
                const tr = report.therapistReport || {};
                const typeLabel = REPORT_LABELS[report.reportType] || report.reportType || 'Report';
                const isDeleting = deletingJobId === report.jobId;
                return (
                  <li key={report.jobId || `${typeLabel}-${report.extractedAt}`} className="p-4 space-y-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="font-medium text-gray-900">{typeLabel}</span>
                      <div className="flex items-center gap-3">
                        {report.extractedAt && (
                          <span className="text-xs text-gray-500">
                            Extracted {new Date(report.extractedAt).toLocaleDateString('en-GB')}
                          </span>
                        )}
                        {report.jobId && (
                          <button
                            type="button"
                            onClick={() => handleDelete(report)}
                            disabled={Boolean(deletingJobId)}
                            className="inline-flex items-center gap-1 rounded-md border border-red-200 px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
                            title="Remove this report"
                          >
                            <Icons.Trash className="h-3.5 w-3.5" />
                            {isDeleting ? 'Removing…' : 'Remove'}
                          </button>
                        )}
                      </div>
                    </div>
                    {tr.summary && <p className="text-sm text-gray-700">{tr.summary}</p>}
                    <div className="grid gap-3 md:grid-cols-2">
                      <ListSection title="Strengths" items={tr.strengths} />
                      <ListSection title="Challenges" items={tr.challenges} />
                      <ListSection title="Identified needs" items={tr.identifiedNeeds} />
                      <ListSection title="Classroom strategies" items={tr.classroomStrategies} />
                      <ListSection title="Recommended interventions" items={tr.recommendedInterventions} />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
